import React, {
    useState,
    useEffect,
    useContext,
    useCallback,
    useMemo,
} from 'react';
import { useTranslation } from 'react-i18next';
import type { ListChildComponentProps } from 'react-window';
import { VariableSizeList } from 'react-window';
import {
    Button,
    Checkbox,
    FormControl,
    Grid,
    ListItem,
    ListItemText,
    Switch,
    Typography,
} from '@material-ui/core';
import type { Theme } from '@material-ui/core';
import { default as DOMPurify } from 'dompurify';

import { Block, Heading } from '@klp/design';
import { useKlpStyles } from '@klp/design/Theme';
import { FormContext } from '@klp/form/FormContext';
import { sortList } from '@klp/helpers';
import { Search, filterSearchResult, highlightSearch } from '@klp/search';

import { ByggKunde } from '../classes';
import type { ByggKundeResult } from '../classes';
import { useGetByggKundeQuery } from '../rtkApi';

const styles = (theme: Theme) => ({
    main: {
        flex: '1 1 auto',
    },
    listContainer: {
        fontSize: theme.klpVariant.small.fontSize,
        border: '2px solid transparent',
        borderColor: theme.palette.primary.medium,
    },
    group: {
        borderTop: '1px solid transparent',
        borderColor: theme.palette.primary.mediumLight,
        '&:first-of-type': {
            borderColor: 'transparent',
        },
    },
    item: {
        paddingLeft: theme.spacing(5),
    },
});

interface ISelectorField {
    key: string;
    name: string;
    secondaryName: string;
}

const selectorFields = {
    kunde: {
        key: 'kundeNummer',
        name: 'kundeNavn',
        secondaryName: 'byggNavn',
    },
    bygg: {
        key: 'byggNummer',
        name: 'byggNavn',
        secondaryName: 'kundeNavn',
    },
};

interface ByggKundeSelectorProps extends Omit<
    React.ComponentProps<'input'>,
    'value' | 'onChange'
> {
    name: string;
    value: Array<ByggKunde>;
    title: string;
    readonly?: boolean;
    primarySelect?: string;
    secondarySelect?: string;
    label?: string;
    error?: boolean;
    margin?: string;
    helperText?: string;
    onChange?: (value: Array<ByggKundeResult>) => void;
}

export const ByggKundeSelector: React.FC<ByggKundeSelectorProps> = ({
    name,
    value,
    title,
    readonly,
    primarySelect,
    secondarySelect,
    onChange,
    ...props
}) => {
    const classes = useKlpStyles({}, styles);
    const context = useContext(FormContext);
    const { t } = useTranslation();

    const { data } = useGetByggKundeQuery({});
    const byggKunde = useMemo(() => data?.byggKunde || [], [data]);

    // Local state
    const [recipients, setRecipients] = useState<Array<ByggKundeResult>>(
        value.map((el) => new ByggKunde(el).toResult()),
    );
    const [search, setSearch] = useState('');
    const [groupBySecondary, setGroupBySecondary] = useState(false);

    // Initial mount
    useEffect(() => {
        if (onChange) {
            onChange(value);
        }
        if (context?.setValue) {
            // @ts-expect-error - FormContext doesn't support array values yet
            context.setValue(name, value);
        }
        if (context?.addError && value.length === 0) {
            context.addError(name);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const getSelectorFields = useCallback((): {
        level1: ISelectorField;
        level2: ISelectorField;
    } => {
        let primarySelectVal = '';
        let secondarySelectVal = '';

        if (primarySelect && secondarySelect) {
            primarySelectVal = primarySelect;
            secondarySelectVal = secondarySelect;
        }
        return {
            level1: selectorFields[
                groupBySecondary ? secondarySelectVal : primarySelectVal
            ],
            level2: selectorFields[
                !groupBySecondary ? secondarySelectVal : primarySelectVal
            ],
        };
    }, [groupBySecondary, primarySelect, secondarySelect]);

    const listSort = useCallback(
        (list: Array<ByggKunde>): Array<ByggKunde> => {
            const { level1, level2 } = getSelectorFields();
            return list.sort(sortList(level1.name, level2.name));
        },
        [getSelectorFields],
    );

    const flattenList = useCallback((list: Array<ByggKunde>) => {
        const flatList: Array<ByggKunde> = [];
        list.forEach((group) => {
            flatList.push(group);
            group.children.forEach((child) => {
                flatList.push(child);
            });
        });
        return flatList;
    }, []);

    const prepareNestedList = useCallback(
        (list: Array<ByggKunde>): Array<ByggKunde> => {
            const { level1, level2 } = getSelectorFields();
            const nestedList: Array<ByggKunde> = [];

            list.forEach((row) => {
                let group = nestedList.find(
                    (el) =>
                        el.group === true && el[level1.key] === row[level1.key],
                );
                if (!group) {
                    group = new ByggKunde({
                        kundeNummer: row.kundeNummer,
                        kundeNavn: row.kundeNavn,
                        byggNummer: row.byggNummer,
                        byggNavn: row.byggNavn,
                        group: true,
                        children: [],
                    });
                    nestedList.push(group);
                }

                const childElement = new ByggKunde({
                    kundeNummer: row.kundeNummer,
                    kundeNavn: row.kundeNavn,
                    byggNummer: row.byggNummer,
                    byggNavn: row.byggNavn,
                });
                childElement.selected = !!recipients.find(
                    (el) =>
                        el[level1.key] === row[level1.key] &&
                        el[level2.key] === row[level2.key],
                );
                group.addChild(childElement);
            });

            return flattenList(nestedList);
        },
        [getSelectorFields, recipients, flattenList],
    );

    const toggleRecord = useCallback(
        (list: Array<ByggKundeResult>, record: ByggKunde) => {
            const { level1, level2 } = getSelectorFields();
            const newSelected = !record.selected;

            if (newSelected) {
                list.push(record.toResult());
            } else {
                list = list.filter(
                    (el) =>
                        !(
                            el[level1.key] === record[level1.key] &&
                            el[level2.key] === record[level2.key]
                        ),
                );
            }
            return list;
        },
        [getSelectorFields],
    );

    const getRecipientsData = useCallback(
        (list: Array<ByggKundeResult>): Array<ByggKunde> => {
            const { level1, level2 } = getSelectorFields();

            if (byggKunde.length === 0) {
                return [];
            }

            return list
                .map((row) => {
                    const found = byggKunde.find(
                        (el) =>
                            el[level1.key] === row[level1.key] &&
                            el[level2.key] === row[level2.key],
                    );
                    return found
                        ? new ByggKunde({
                              kundeNummer: found.kundeNummer,
                              kundeNavn: found.kundeNavn,
                              byggNummer: found.byggNummer,
                              byggNavn: found.byggNavn,
                          })
                        : null;
                })
                .filter(Boolean) as Array<ByggKunde>;
        },
        [getSelectorFields, byggKunde],
    );

    const handleSearch = (searchValue: string) => {
        setSearch(searchValue);
    };

    const handleGroupChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setGroupBySecondary(event.target.checked);
    };

    const handleResetSelection = () => {
        setRecipients([]);
    };

    const handleToggleRowGroup = useCallback(
        (record: ByggKunde) => () => {
            const { level1 } = getSelectorFields();

            let list = recipients.filter(
                (row) => row[level1.key] !== record[level1.key],
            );

            record.children.forEach((el) => {
                el.selected = record.selected;
                list = toggleRecord(list, el);
            });

            if (onChange) {
                onChange(list);
            }
            if (context?.setValue) {
                // @ts-expect-error - FormContext doesn't support array values yet
                context.setValue(name, list);
            }
            setRecipients(list);
        },
        [getSelectorFields, recipients, toggleRecord, onChange, context, name],
    );

    const handleToggleRow = useCallback(
        (record: ByggKunde) => () => {
            const list = toggleRecord([...recipients], record);

            if (onChange) {
                onChange(list);
            }
            if (context?.setValue && onChange) {
                onChange(list);
                // @ts-expect-error - FormContext doesn't support array values yet
                context.setValue(name, list);
            }
            setRecipients(list);
        },
        [recipients, toggleRecord, onChange, context, name],
    );

    const getNestedListRowSize = () => {
        return 40;
    };

    const renderNestedListRow = useCallback(
        (
            list: Array<ByggKunde>,
            level1: ISelectorField,
            level2: ISelectorField,
            listId: string,
        ) =>
            (listProps: ListChildComponentProps) => {
                const { index, style } = listProps;

                const record = list[index];
                const { group } = record;

                const recordSignature = group
                    ? `${record[level1.key]}`
                    : `${record[level1.key]}-${record[level2.key]}`;

                const titleValue = group
                    ? record[level1.name]
                    : record[level2.name];
                const checkboxColor = group ? 'secondary' : 'primary';

                const clickHandler = group
                    ? handleToggleRowGroup(record)
                    : handleToggleRow(record);

                const checked = group
                    ? record.children.reduce(
                          (prev, el) => prev && el.selected,
                          true,
                      )
                    : record.selected;

                return (
                    <ListItem
                        button={true}
                        style={style}
                        key={index}
                        dense={true}
                        onClick={clickHandler}
                        disabled={readonly}
                        className={group ? classes.group : classes.item}
                        data-record={recordSignature}
                        data-testid-list={listId}
                        data-testid-group={record.group}
                    >
                        <Checkbox
                            tabIndex={-1}
                            disableRipple={true}
                            checked={checked}
                            color={checkboxColor}
                        />
                        <ListItemText
                            disableTypography={true}
                            primary={
                                <span
                                    dangerouslySetInnerHTML={{
                                        __html: DOMPurify.sanitize(
                                            highlightSearch(titleValue, search),
                                        ),
                                    }}
                                />
                            }
                            primaryTypographyProps={{
                                variant: 'overline',
                            }}
                        />
                    </ListItem>
                );
            },
        [
            handleToggleRowGroup,
            handleToggleRow,
            readonly,
            classes.group,
            classes.item,
            search,
        ],
    );

    const renderNestedList = useCallback(
        (list: Array<ByggKunde>, listId: string) => {
            const { level1, level2 } = getSelectorFields();
            const nestedList: Array<ByggKunde> = prepareNestedList(list);

            return (
                <Block className={classes.listContainer}>
                    <div className="ByggKundeSelector__List">
                        <VariableSizeList
                            height={400}
                            width="100%"
                            itemSize={getNestedListRowSize}
                            itemCount={nestedList.length}
                        >
                            {renderNestedListRow(
                                nestedList,
                                level1,
                                level2,
                                listId,
                            )}
                        </VariableSizeList>
                    </div>
                </Block>
            );
        },
        [
            getSelectorFields,
            prepareNestedList,
            classes.listContainer,
            renderNestedListRow,
        ],
    );

    const renderSortedNestedList = useCallback(
        (list: Array<ByggKunde>, listId: string) => {
            const { level1, level2 } = getSelectorFields();

            const safeCopyList = list.map(
                (item) =>
                    new ByggKunde({
                        kundeNummer: item.kundeNummer,
                        kundeNavn: item.kundeNavn,
                        byggNummer: item.byggNummer,
                        byggNavn: item.byggNavn,
                    }),
            );

            const filteredList = filterSearchResult(
                listSort(safeCopyList),
                search,
                [level1.key, level1.name, level2.key, level2.name],
            );
            return renderNestedList(filteredList, listId);
        },
        [getSelectorFields, listSort, search, renderNestedList],
    );

    const renderRecipientsList = useCallback(
        (list: Array<ByggKundeResult>, listId: string) => {
            return renderNestedList(
                listSort(flattenList(getRecipientsData(list))),
                listId,
            );
        },
        [renderNestedList, listSort, flattenList, getRecipientsData],
    );

    const renderTitle = () => {
        return (
            <Heading
                klpHeadingLevel="FOUR"
                translation={title}
                marginBottom="tiny"
            />
        );
    };

    const renderSearchForm = () => (
        <Search
            id="ByggKundeSearch"
            searchHandler={handleSearch}
            searchOnType={true}
            placeholder="buildingClientChooser.recipients.search"
        />
    );

    const renderListTitle = (titleKey: 'all' | 'selected') => (
        <Heading
            klpHeadingLevel="FIVE"
            translation={`buildingClientChooser.recipients.${titleKey}`}
            marginBottom="tiny"
        />
    );

    const renderSwitchGrouping = () => (
        <Block>
            <Typography variant="subtitle2" component="span">
                {t('buildingClientChooser.groupByClient')}
            </Typography>
            <Switch
                value="checked"
                color="secondary"
                checked={groupBySecondary}
                onChange={handleGroupChange}
            />
            <Typography variant="subtitle2" component="span">
                {t('buildingClientChooser.groupByBuilding')}
            </Typography>
        </Block>
    );

    if (!byggKunde) {
        return null;
    }

    return (
        <Grid
            item={true}
            xs={12}
            className={classes.main}
            data-testid={props['data-testid']}
        >
            {readonly && (
                <Grid
                    container={true}
                    spacing={2}
                    justifyContent="flex-start"
                    alignItems="stretch"
                >
                    <Grid item={true} xs={12} sm={6}>
                        {renderTitle()}
                        {renderSortedNestedList(
                            getRecipientsData(recipients),
                            'all',
                        )}
                    </Grid>
                </Grid>
            )}

            {!readonly && (
                <FormControl>
                    <Grid
                        container={true}
                        spacing={2}
                        justifyContent="flex-start"
                        alignItems="stretch"
                    >
                        <Grid item={true} xs={12}>
                            {renderTitle()}
                            {renderSearchForm()}
                            {renderSwitchGrouping()}
                        </Grid>
                        <Grid item={true} xs={12} sm={6}>
                            {renderListTitle('all')}
                            {renderSortedNestedList(byggKunde, 'all')}
                        </Grid>
                        <Grid item={true} xs={12} sm={6}>
                            {renderListTitle('selected')}
                            {renderRecipientsList(recipients, 'recipients')}
                            <Grid container={true} direction="column">
                                <Grid
                                    item={true}
                                    xs={12}
                                    className="ByggKundeSelector__Reset"
                                >
                                    <Block align="right" marginTop="tiny">
                                        <Button
                                            disabled={recipients.length === 0}
                                            variant="outlined"
                                            onClick={handleResetSelection}
                                        >
                                            {t('general.clearList')}
                                        </Button>
                                    </Block>
                                </Grid>
                            </Grid>
                        </Grid>
                    </Grid>
                </FormControl>
            )}
        </Grid>
    );
};

ByggKundeSelector.displayName = 'ByggKundeSelector';
